import { LazyExoticComponent, FC } from 'react';
import { lazyWithPreload as lazy } from 'react-lazy-with-preload';
import { routePath, authPaths } from 'config/route-config';

import DirectSalesLanding from 'components/direct-sales-landing/DirectSalesLanding';
import EnergyNeeds from 'components/direct-sales/energy-needs/EnergyNeeds';
import PersonalDetails from 'components/direct-sales/personal-details/PersonalDetails';
import DirectDebit from 'components/direct-sales/direct-debit-revamp/DirectDebit';
import MeterpointValidation from 'components/direct-sales/meterpoint-validation/MeterPointValidationRevamp';
import MeterpointGuide from 'components/direct-sales/meterpoint-guide/MeterpointGuideRevamp';
import SelectOptions from 'components/direct-sales/select-options/SelectOptionsRevamp';
import BusinessAnnouncement from 'components/direct-sales/business-updates-revamp/BusinessAnnouncement';
import Quote from 'components/direct-sales/quote/QuoteRevamp';
import PeakSave from 'components/direct-sales/peak-save/PeakSave';
import OrderSummary from 'components/direct-sales/order-summary-revamp/OrderSummary';
import OrderComplete from 'components/direct-sales/order-complete/OrderCompleteRevamp';
import Psr from 'components/direct-sales/psr-entry/PsrRevamp';
import EmailQuote from 'components/direct-sales/email-quote/EmailQuote';
import RetrieveQuote from 'components/retrieve-quote/RetrieveQuote';
import QuoteExpired from 'components/direct-sales/quote-expired/QuoteExpired';
import EmailCapture from 'components/direct-sales/email-capture/EmailCapture';
import AbandonedCart from 'components/abandoned-cart/AbandonedCart';
import SaleHandoff from 'components/sale-handoff/SaleHandoff';
import CreateAccount from 'components/direct-sales/create-account/CreateAccount';
import UserPreferences from 'components/direct-sales/user-preferences/UserPreferences';
import ConsumptionEntry from 'components/direct-sales/consumption-entry/ConsumptionEntry';

export type ScreenParts = {
  Template?: Component;
  Content?: Component;
  RightSidebar?: Component;
  LeftSidebar?: Component;
  Header?: Component;
  Skyline?: Component;
  Footer?: Component;
};

export type Component = (
  | FC<ScreenParts>
  | LazyExoticComponent<FC<ScreenParts>>
) & {
  preload?: () => Promise<unknown>;
};

export type Route = {
  path: string;
  name: string;
  step?: number;
  featureFlag?: string;
  isLanding?: boolean;
  isEnding?: boolean;
  noIndex?: boolean;
  component?: Component;
} & ScreenParts;

export const authRoutes = [
  {
    name: 'Auth Response',
    path: authPaths.authResponse,
    Content: lazy(() => import('components/app/routes/AuthResponse')),
    isLanding: true,
    noIndex: true
  }
];

export const partnerLandingRoutes: Route[] = [
  {
    name: 'Direct Sales',
    path: routePath.home,
    step: 1,
    isLanding: true,
    Content: DirectSalesLanding.Content,
    Header: DirectSalesLanding.Header
  },
  {
    name: 'Quote by Email',
    path: routePath.retrieveQuote,
    step: 1,
    Content: RetrieveQuote.Content,
    Header: RetrieveQuote.Header,
    isLanding: true,
    noIndex: true
  },
  {
    name: 'Abandoned Cart',
    path: routePath.abandonedCart,
    step: 1,
    isLanding: true,
    Content: AbandonedCart.Content,
    noIndex: true
  },
  {
    name: 'Sale Handoff',
    path: routePath.saleHandoff,
    step: 1,
    isLanding: true,
    Content: SaleHandoff.Content,
    Header: SaleHandoff.Header,
    noIndex: true
  }
];

export const journeyRoutes: Route[] = [
  {
    name: 'Business Announcement',
    path: routePath.businessUpdatesAnnouncement,
    Content: BusinessAnnouncement.Content,
    Header: BusinessAnnouncement.Header
  },
  {
    name: 'Select Options',
    path: routePath.selectOptions,
    Content: SelectOptions.Content,
    Header: SelectOptions.Header
  },
  {
    name: 'Meterpoint Validation',
    path: routePath.meterpointValidation,
    step: 2,
    Content: MeterpointValidation.Content,
    Header: MeterpointValidation.Header
  },
  {
    name: 'Meterpoint Guide',
    path: routePath.meterpointGuide,
    step: 2,
    Content: MeterpointGuide.Content,
    Header: MeterpointGuide.Header
  },
  {
    name: 'Your energy needs',
    path: routePath.energyNeeds,
    step: 2,
    Content: EnergyNeeds.Content,
    Header: EnergyNeeds.Header
  },
  {
    name: 'Your tariff quote',
    path: routePath.quote,
    step: 3,
    Template: Quote.Template,
    Skyline: Quote.Skyline,
    Content: Quote.Content,
    LeftSidebar: Quote.LeftSidebar,
    Header: Quote.Header,
    Footer: Quote.Footer
  },
  {
    name: 'Peak Save',
    path: routePath.peakSave,
    featureFlag: 'releasePeakSaveJourney',
    Header: PeakSave.Header,
    Content: PeakSave.Content,
    Footer: PeakSave.Footer,
    Template: PeakSave.Template
  },
  {
    name: 'Email Quote',
    path: routePath.emailQuote,
    Header: EmailQuote.Header,
    Content: EmailQuote.Content
  },
  {
    name: 'Quote expired',
    path: routePath.quoteExpired,
    step: 1,
    Content: QuoteExpired.Content,
    Footer: QuoteExpired.Footer
  },
  {
    name: 'Account set up',
    path: routePath.personalDetails,
    step: 4,
    Content: PersonalDetails.Content,
    RightSidebar: PersonalDetails.RightSidebar,
    Header: PersonalDetails.Header
  },
  {
    name: 'Payment details',
    path: routePath.directDebit,
    step: 4,
    Content: DirectDebit.Content,
    RightSidebar: DirectDebit.RightSidebar,
    Header: DirectDebit.Header,
    Footer: DirectDebit.Footer
  },
  {
    name: 'Priority Services Register',
    path: routePath.psrEntry,
    step: 4,
    Content: Psr.Content,
    RightSidebar: Psr.RightSidebar,
    Header: Psr.Header
  },
  {
    name: 'Check your order',
    path: routePath.orderSummary,
    step: 4,
    Content: OrderSummary.Content,
    Header: OrderSummary.Header,
    Template: OrderSummary.Template
  },
  {
    name: 'Confirmation',
    path: routePath.orderComplete,
    isEnding: true,
    Content: OrderComplete.Content,
    Template: OrderComplete.Template
  },
  {
    name: 'Email Capture',
    path: routePath.emailCapture,
    Header: EmailCapture.Header,
    Content: EmailCapture.Content
  },
  {
    name: 'Quote Expired Abandoned Cart',
    path: routePath.quoteExpiredAbandonedCart,
    Content: QuoteExpired.Content,
    Footer: QuoteExpired.Footer
  },
  {
    name: 'Sales Information',
    path: routePath.salesInformation,
    Header: PeakSave.Header,
    Content: PeakSave.Content,
    Footer: PeakSave.Footer,
    Template: PeakSave.Template
  },
  {
    name: 'Consumption Entry',
    path: routePath.consumptionEntry,
    Content: ConsumptionEntry.Content,
    Template: ConsumptionEntry.Template
  },
  {
    name: 'Create account',
    path: routePath.createAccount,
    Content: CreateAccount.Content,
    Template: CreateAccount.Template
  },
  {
    name: 'User preferences',
    path: routePath.userPreferences,
    Content: UserPreferences.Content,
    Template: UserPreferences.Template
  }
];

export const routes = [
  ...journeyRoutes,
  ...partnerLandingRoutes,
  ...authRoutes
];
